// Community moderation UI shared by a game's moderation tab and the moderators' site-wide queue
// (/dashboard/moderation, web wave 8). Rules and pure helpers: src/moderation.ts. Community stays the authority on who
// may do what; the buttons only follow `allowed`.

import { api, ApiError } from "./api.ts";
import { getCatalogue } from "./catalogue.ts";
import { fmtAgo } from "./clips.ts";
import { clear, errorBox, fmtDate, h, link, toast } from "./dom.ts";
import {
  ACTION_DONE,
  AGE_FILTERS,
  allowed,
  canUnhide,
  filterQueue,
  hiddenTargets,
  modError,
  parseLog,
  parseQueue,
  queueFilterFromQuery,
  queueFilterToQuery,
  READABLE_TYPES,
  reasonLabel,
  targetKey,
  titleFallback,
  titleFromRead,
  titlesFromQueue,
  TYPE_LABEL,
  typeLabel,
  waitingSince,
  type Hidden,
  type LogEntry,
  type ModAction,
  type Queue,
  type QueueFilter,
  type QueueItem,
  type Scope,
} from "./moderation.ts";

export type ModFn = (a: ModAction, target: Record<string, unknown>, out: HTMLElement) => void;

/** One reported target with its reports and the actions this viewer may take. `gameTitle`: the site-wide view. */
export function queueRow(it: QueueItem, scope: Scope, mod: ModFn, gameTitle?: string | null): HTMLElement {
  const can = allowed(scope, it.target_type);
  const out = h("div", { "aria-live": "polite" });
  const target = { target_type: it.target_type, target_id: it.target_id };
  const since = waitingSince(it);
  return h(
    "li",
    { class: "review" },
    h(
      "div",
      { class: "review-head" },
      h("span", { class: "badge" }, typeLabel(it.target_type)),
      h("strong", null, `${it.reports.length} ${it.reports.length === 1 ? "report" : "reports"}`),
      h("span", { class: "dim small" }, [...new Set(it.reports.map((r) => reasonLabel(r.reason)))].join(", ")),
      gameTitle !== undefined && it.game_id ? h("span", { class: "small" }, "on ", link(`/g/${it.game_id}`, null, gameTitle ?? "a game")) : null,
      gameTitle !== undefined && since ? h("span", { class: "dim small" }, `first reported ${fmtAgo(since)}`) : null,
    ),
    h("p", null, it.preview || h("span", { class: "dim" }, titleFallback(it.target_type))),
    it.reports.some((r) => r.note) ? h("ul", { class: "plain small dim" }, it.reports.filter((r) => r.note).map((r) => h("li", null, `${reasonLabel(r.reason)}: ${r.note}`))) : null,
    h(
      "div",
      { class: "actions" },
      can.includes("resolve_burst") ? h("button", { type: "button", class: "btn small", onclick: () => mod("resolve_burst", { burst_id: it.target_id }, out) }, "Resolve burst") : null,
      can.includes("hide") && it.state !== "hidden" ? h("button", { type: "button", class: "btn small", onclick: () => mod("hide", target, out) }, "Hide") : null,
      can.includes("unhide") && it.state === "hidden" ? h("button", { type: "button", class: "btn small", onclick: () => mod("unhide", target, out) }, "Unhide") : null,
      can.includes("dismiss") ? h("button", { type: "button", class: "btn small ghost", onclick: () => mod("dismiss", target, out) }, "Dismiss") : null,
      can.includes("remove") ? h("button", { type: "button", class: "btn small danger", onclick: () => mod("remove", target, out) }, "Remove") : null,
    ),
    scope === "creator" && it.target_type === "review" ? h("p", { class: "dim small" }, "Reviews can only be hidden by moderators. Dismiss the reports, or leave them for a moderator.") : null,
    scope === "creator" && it.target_type === "review_burst" ? h("p", { class: "dim small" }, "A wave of reviews: a moderator looks at it.") : null,
    out,
  );
}

/** Calls community's moderation action, then reloads; refusals show in plain words next to the item. */
export function modAction(reload: () => void): ModFn {
  return async (action, target, out) => {
    if (action === "remove" && !confirm("Remove for good? This can't be undone.")) return;
    try {
      await api("community", "/moderation/actions", { body: { action, ...target } });
      toast(ACTION_DONE[action]);
      reload();
    } catch (e) {
      const words = e instanceof ApiError ? modError(e.code) : null;
      clear(out, words ? h("p", { class: "error", role: "alert" }, words) : errorBox(e));
    }
  };
}

/** How many hidden items get a single read for their title (the rest use the queue's preview or a plain label). */
export const TITLE_READS = 24;

/**
 * Titles for hidden items, never ids: community's actioned queue (one call: `preview` is the target's title, or its
 * body) first, then single reads of threads, bugs and clips (moderators and the game's creator can open hidden ones).
 * Reviews and replies have no single read, so without a preview they get a plain label.
 */
export async function hiddenTitles(hidden: Hidden[], gameId: string | null): Promise<Map<string, string>> {
  if (!hidden.length) return new Map();
  const titles = await api("community", `/moderation/queue?status=actioned${gameId ? `&game=${encodeURIComponent(gameId)}` : ""}`)
    .then((r) => titlesFromQueue(parseQueue(r).items))
    .catch(() => new Map<string, string>());
  const path: Record<string, string> = { thread: "/threads/", bug: "/bugs/", clip: "/clips/" };
  const missing = hidden.filter((x) => !titles.has(targetKey(x.target_type, x.target_id)) && READABLE_TYPES.has(x.target_type)).slice(0, TITLE_READS);
  await Promise.all(
    missing.map((x) =>
      api("community", path[x.target_type]! + encodeURIComponent(x.target_id))
        .then((r) => {
          const t = titleFromRead(x.target_type, r);
          if (t) titles.set(targetKey(x.target_type, x.target_id), t);
        })
        .catch(() => {}),
    ),
  );
  return titles;
}

/** Who hid it, in words for this viewer. */
function hiddenBy(x: Hidden, scope: Scope): string {
  if (!x.by) return "";
  return ` by ${x.by === "creator" ? (scope === "creator" ? "you" : "the creator") : x.by === "moderator" ? "a moderator" : "Banger"}`;
}

/** The Hidden list: title, when and by whom, and Unhide where this viewer may. `games`: titles for the site-wide view. */
export function hiddenList(hidden: Hidden[], titles: Map<string, string>, scope: Scope, mod: ModFn, games?: Map<string, string>): HTMLElement {
  return h(
    "ul",
    { class: "reviews" },
    hidden.map((x) => {
      const out = h("div", { "aria-live": "polite" });
      const title = titles.get(targetKey(x.target_type, x.target_id));
      return h(
        "li",
        { class: "review" },
        h(
          "div",
          { class: "review-head" },
          h("span", { class: "badge" }, typeLabel(x.target_type)),
          games && x.game_id ? h("span", { class: "small" }, "on ", link(`/g/${x.game_id}`, null, games.get(x.game_id) ?? "a game")) : null,
          h("span", { class: "dim small" }, `hidden ${fmtDate(x.at)}${hiddenBy(x, scope)}`),
        ),
        h("p", null, title ?? h("span", { class: "dim" }, titleFallback(x.target_type))),
        canUnhide(scope, x)
          ? h("div", { class: "actions" }, h("button", { type: "button", class: "btn small ghost", onclick: () => mod("unhide", { target_type: x.target_type, target_id: x.target_id }, out) }, "Unhide"))
          : h("p", { class: "dim small" }, x.target_type === "review" ? "Only a moderator can unhide a review." : "Hidden by a moderator: only a moderator can bring it back."),
        out,
      );
    }),
  );
}

// ---- who is a community moderator ----------------------------------------------------------------------------

/**
 * Community moderators get the unscoped open queue; everyone else `403 not_moderator`. The dashboard nav asks once per
 * user and page load, and the site-wide view reuses that answer for its first draw (so the queue isn't fetched twice).
 */
let probe: { key: string; queue: Promise<Queue>; ok: Promise<boolean>; fresh: boolean; at: number } | null = null;

export function isCommunityModerator(userId: string): Promise<boolean> {
  if (probe?.key !== userId) {
    const queue = api("community", "/moderation/queue?status=open").then(parseQueue);
    probe = { key: userId, queue, ok: queue.then((q) => q.scope === "moderator", () => false), fresh: true, at: Date.now() };
  }
  return probe.ok;
}

function takeProbedQueue(userId: string, now = Date.now()): Promise<Queue> | null {
  if (!probe || probe.key !== userId || !probe.fresh || now - probe.at > 30_000) return null;
  probe.fresh = false;
  return probe.queue;
}

// ---- the site-wide queue -------------------------------------------------------------------------------------

/**
 * Every open report across all games, for thrdm moderators. Filters (game, type, age) live in the query string so a
 * view can be shared; they filter the one answer in the page. Community returns at most 500 open reports, oldest first.
 */
export function moderatorQueueView(userId: string, query: URLSearchParams): HTMLElement {
  const host = h("div", null, h("p", { class: "dim" }, "Loading…"));
  let f: QueueFilter = queueFilterFromQuery(query);
  const load = async (first: boolean) => {
    let q: Queue;
    try {
      q = await ((first && takeProbedQueue(userId)) || api("community", "/moderation/queue?status=open").then(parseQueue));
    } catch (e) {
      if (e instanceof ApiError && (e.code === "not_moderator" || e.status === 403))
        return clear(host, h("p", null, "Only Banger moderators see reports from every game. A game's creator finds its reports on that game's Community page."));
      return clear(host, errorBox(e));
    }
    if (!first && !host.isConnected) return;
    if (q.scope !== "moderator") return clear(host, h("p", null, "Only Banger moderators see reports from every game."));
    const cat = await getCatalogue().catch(() => null);
    const games = new Map<string, string>((cat?.games ?? []).map((g) => [g.id, g.title]));
    const mod = modAction(() => void load(false));
    const list = h("div");
    const gameIds = [...new Set(q.items.map((it) => it.game_id).filter(Boolean))].sort((a, b) => (games.get(a) ?? a).localeCompare(games.get(b) ?? b));
    const types = [...new Set(q.items.map((it) => it.target_type))];
    const select = (name: string, label: string, options: [string, string][], value: string, set: (v: string) => void) =>
      h(
        "label",
        { class: "field" },
        h("span", null, label),
        h(
          "select",
          { name, onchange: (e: Event) => (set((e.target as HTMLSelectElement).value), draw()) },
          options.map(([v, t]) => h("option", { value: v, selected: v === value }, t)),
        ),
      );
    const draw = () => {
      if (!host.isConnected) return; // navigated away meanwhile
      history.replaceState(null, "", location.pathname + queueFilterToQuery(f));
      const shown = filterQueue(q.items, f);
      clear(
        list,
        h("p", { class: "dim small", "aria-live": "polite" }, `${shown.length} of ${q.items.length} reported ${q.items.length === 1 ? "item" : "items"}`),
        shown.length ? h("ul", { class: "reviews" }, shown.map((it) => queueRow(it, q.scope, mod, games.get(it.game_id) ?? null))) : h("p", { class: "dim" }, q.items.length ? "Nothing matches these filters." : "Nothing to review."),
      );
    };
    const filters = h(
      "form",
      { class: "field-row", onsubmit: (e: Event) => e.preventDefault() },
      select("game", "Game", [["", "All games"], ...gameIds.map((id): [string, string] => [id, games.get(id) ?? id])], f.game ?? "", (v) => (f = { ...f, game: v || null })),
      select("type", "Type", [["", "All types"], ...Object.entries(TYPE_LABEL).filter(([t]) => types.includes(t) || t === f.type)], f.type ?? "", (v) => (f = { ...f, type: v || null })),
      select("age", "Age", AGE_FILTERS, f.age, (v) => (f = { ...f, age: v as QueueFilter["age"] })),
    );
    clear(host, filters, list, recentlyHidden(games, mod));
    draw();
  };
  void load(true);
  return h(
    "div",
    null,
    h("h1", null, "Reports"),
    h("p", { class: "dim" }, "Open reports from every game's page: reviews, threads, replies, bug reports and clips. Hide, dismiss or remove; a game's creator sees only their own game."),
    host,
  );
}

/** Hidden items across every game (the unscoped log), newest first, with titles. */
function recentlyHidden(games: Map<string, string>, mod: ModFn): HTMLElement {
  const el = h("section", { class: "section", hidden: true });
  void (async () => {
    const log = await api("community", "/moderation/log?limit=200").then(parseLog, () => [] as LogEntry[]);
    const hidden = hiddenTargets(log, null).slice(0, 50);
    if (!hidden.length) return;
    const titles = await hiddenTitles(hidden, null);
    clear(el, h("h2", null, "Recently hidden"), hiddenList(hidden, titles, "moderator", mod, games));
    el.hidden = false;
  })().catch(() => {});
  return el;
}
